import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Search, User, MessageSquare, Crown, LogOut, LifeBuoy, Star,
  BookOpen, ChevronDown, ChevronRight, CreditCard, History, Building2, FileText
} from 'lucide-react';
import { authService } from '../services/api';

import { useToast } from '../hooks/useToast';
import authStorage from '../utils/authStorage';

const MyAccountLayout = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const user = JSON.parse(authStorage.getItem('user') || '{}');
  const initialDisplayName = user.name || (user.emailId || user.username || 'demo');

  const [displayName, setDisplayName] = useState(initialDisplayName);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState(user.photoUrl || '');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Sidebar group expand/collapse state — dynamically open based on active path
  const [vendorOpen, setVendorOpen] = useState(!location.pathname.startsWith('/my-account/magazine'));
  const [magazineOpen, setMagazineOpen] = useState(location.pathname.startsWith('/my-account/magazine'));

  const fetchUserProfile = async () => {
    const email = user.emailId || user.username;
    if (email && email.includes('@')) {
      const res = await authService.getUserProfile(email);
      if (res.success && res.data) {
        const profile = res.data;
        if (profile.name) setDisplayName(profile.name);
        if (profile.photoUrl) setProfilePhotoUrl(profile.photoUrl);
        authStorage.setItem('user', JSON.stringify({
          ...user,
          id: profile.id,
          name: profile.name,
          emailId: profile.emailId,
          mobileNo: profile.mobileNo || '',
          photoUrl: profile.photoUrl || ''
        }));
      }
    }
  };

  useEffect(() => {
    fetchUserProfile();
    const syncProfilePhoto = () => {
      const u = JSON.parse(authStorage.getItem('user') || '{}');
      setProfilePhotoUrl(u.photoUrl || '');
    };
    window.addEventListener('profilePhotoChanged', syncProfilePhoto);
    return () => window.removeEventListener('profilePhotoChanged', syncProfilePhoto);
  }, []);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('photo', file);
    setUploading(true);
    try {
      const res = await authService.uploadProfilePhoto(formData);
      if (res.success) {
        toast.success('Profile picture updated successfully!');
        if (res.data?.photoUrl) {
          const storedUser = authStorage.getItem('user');
          if (storedUser) {
            const userObj = JSON.parse(storedUser);
            userObj.photoUrl = res.data.photoUrl;
            authStorage.setItem('user', JSON.stringify(userObj));
          }
          setProfilePhotoUrl(res.data.photoUrl);
          window.dispatchEvent(new Event('profilePhotoChanged'));
        }
      } else {
        toast.error('Failed to upload: ' + (res.error || 'Upload error'));
      }
    } catch (err) {
      console.error(err);
      toast.error('An error occurred during upload.');
    } finally {
      setUploading(false);
    }
  };

  const triggerFileSelect = () => fileInputRef.current.click();

  const handleLogout = () => {
    authStorage.removeItem('token');
    authStorage.removeItem('refreshToken');
    authStorage.removeItem('user');
    navigate('/login');
  };

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const base = isLocal ? 'http://localhost:8080' : '';
    if (url.startsWith('/listings/') || url.startsWith('/static/')) return `${base}${url}`;
    return `${base}/listings${url.startsWith('/') ? '' : '/'}${url}`;
  };

  // Active link style helper
  const linkClass = (isActive) =>
    `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
      isActive ? 'bg-[#e0f7f8] text-[#2ab391]' : 'text-gray-700 hover:bg-gray-50'
    }`;

  // Child link style (slightly indented, smaller)
  const childLinkClass = (isActive) =>
    `flex items-center gap-3 pl-8 pr-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
      isActive ? 'bg-[#e0f7f8] text-[#2ab391]' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
    }`;

  // Group header style
  const groupHeaderClass = (isOpen, hasActive) =>
    `flex items-center justify-between w-full px-4 py-3 rounded-lg text-sm font-semibold transition-colors cursor-pointer ${
      hasActive ? 'text-[#2ab391]' : 'text-gray-700 hover:bg-gray-50'
    }`;

  // Check if any child of Vendor group is active
  const vendorPaths = ['/my-account/profile', '/my-account/manage-listing', '/my-account/inquiries', '/my-account/upgrade-membership', '/my-account/reviews'];
  const isVendorActive = vendorPaths.some((p) => location.pathname.startsWith(p));

  // Check if any child of Magazine group is active
  const magazinePaths = ['/my-account/magazine'];
  const isMagazineActive = magazinePaths.some((p) => location.pathname.startsWith(p));

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* User Profile Summary */}
      <div className="p-6 flex flex-col items-center border-b border-gray-100">
        <div className="w-24 h-24 bg-[#b0e0e6] rounded-full flex items-center justify-center mb-3 overflow-hidden border-4 border-[#e0f7f8]">
          {profilePhotoUrl ? (
            <img src={getImageUrl(profilePhotoUrl)} alt="User Profile" className="w-full h-full object-cover" />
          ) : (
            <User size={48} className="text-white" />
          )}
        </div>
        <h3 className="font-bold text-gray-800 text-lg text-center break-all">{displayName}</h3>
        <button
          onClick={triggerFileSelect}
          disabled={uploading}
          className="mt-2 text-xs font-medium text-gray-600 border border-gray-300 rounded-full px-4 py-1.5 hover:bg-gray-50 transition-colors cursor-pointer"
        >
          {uploading ? 'Uploading...' : (profilePhotoUrl ? 'Change Profile Picture' : 'Upload Profile Picture')}
        </button>
        <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" className="hidden" />
      </div>

      {/* Navigation */}
      <div className="flex-1 p-3 space-y-1 overflow-y-auto">

        {/* Search Vendor — top-level link */}
        <NavLink to="/listing" className={({ isActive }) => linkClass(isActive)}>
          <Search size={18} />
          Search Vendor
        </NavLink>

        {/* ── Vendor Group ──────────────────────────────────── */}
        <div>
          <button
            onClick={() => setVendorOpen((v) => !v)}
            className={groupHeaderClass(vendorOpen, isVendorActive)}
          >
            <span className="flex items-center gap-3">
              <Building2 size={18} className={isVendorActive ? 'text-[#2ab391]' : 'text-gray-500'} />
              Vendor
            </span>
            {vendorOpen
              ? <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />
              : <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
            }
          </button>

          {vendorOpen && (
            <div className="mt-1 space-y-0.5">
              <NavLink to="/my-account/profile" className={({ isActive }) => childLinkClass(isActive)}>
                <User size={16} />
                My Profile
              </NavLink>
              <NavLink to="/my-account/manage-listing" className={({ isActive }) => childLinkClass(isActive)}>
                <FileText size={16} />
                Vendor Listing
              </NavLink>
              <NavLink to="/my-account/inquiries" className={({ isActive }) => childLinkClass(isActive)}>
                <MessageSquare size={16} />
                Inquiry
              </NavLink>
              <NavLink to="/my-account/upgrade-membership" className={({ isActive }) => childLinkClass(isActive)}>
                <Crown size={16} />
                Upgrade Membership
              </NavLink>
              <NavLink to="/my-account/reviews" className={({ isActive }) => childLinkClass(isActive)}>
                <Star size={16} />
                Reviews
              </NavLink>
            </div>
          )}
        </div>


        {/* ── Magazine Group ────────────────────────────────── */}
        <div>
          <button
            onClick={() => setMagazineOpen((m) => !m)}
            className={groupHeaderClass(magazineOpen, isMagazineActive)}
          >
            <span className="flex items-center gap-3">
              <BookOpen size={18} className={isMagazineActive ? 'text-[#2ab391]' : 'text-gray-500'} />
              Magazine
            </span>
            {magazineOpen
              ? <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />
              : <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
            }
          </button>

          {magazineOpen && (
            <div className="mt-1 space-y-0.5">
              <NavLink to="/my-account/magazine/subscription" className={({ isActive }) => childLinkClass(isActive)}>
                <CreditCard size={16} />
                Your Subscription
              </NavLink>
              <NavLink to="/my-account/magazine/subscribe" className={({ isActive }) => childLinkClass(isActive)}>
                <BookOpen size={16} />
                Subscribe
              </NavLink>
              <NavLink to="/my-account/magazine/history" className={({ isActive }) => childLinkClass(isActive)}>
                <History size={16} />
                History of Payment
              </NavLink>
            </div>
          )}
        </div>

        {/* Help & Support — top-level link */}
        <NavLink to="/my-account/support-tickets" className={({ isActive }) => linkClass(isActive)}>
          <LifeBuoy size={18} />
          Help &amp; Support
        </NavLink>
      </div>

      {/* Sign Out */}
      <div className="p-3 border-t border-gray-100">
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 transition-colors"
        >
          <LogOut size={18} />
          Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="bg-[#f8f9fa] min-h-screen py-4 sm:py-8 pt-24 sm:pt-32">
      <div className="container mx-auto px-3 sm:px-4 lg:px-8 max-w-7xl flex flex-col md:flex-row gap-4 sm:gap-6">

        {/* ── Desktop Sidebar ──────────────────────────────── */}
        <div className="hidden md:block w-full md:w-64 flex-shrink-0">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <SidebarContent />
          </div>
        </div>

        {/* ── Main Content Area ────────────────────────────── */}
        <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 min-h-[600px] min-w-0">
          <Outlet />
        </div>

      </div>
    </div>
  );
};

export default MyAccountLayout;
